import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, CircleHelp, ExternalLink, X } from 'lucide-react'
import { ErrorState } from '@comforthub/design-system'
import SideDrawer from '../SideDrawer'
import { apiErrorMessage } from '../../lib/apiError'
import {
  HELP_STALE_MS,
  findArticleForPath,
  helpApi,
  helpKeys,
  helpPath,
  helpQueryRetry,
  stripLeadingTitle,
  type HelpSide,
} from '../../lib/help'
import HelpMarkdown from './HelpMarkdown'
import { useHelpArticles } from './useHelp'

/**
 * The article in a right-side drawer. A `help:` link inside opens the target
 * here too (Back returns), and Open in help centre goes to its page.
 */
export function HelpArticleDrawer({
  side,
  slug: firstSlug,
  onClose,
}: {
  side: HelpSide
  slug: string
  onClose: () => void
}) {
  const [trail, setTrail] = useState<string[]>([firstSlug])
  const slug = trail[trail.length - 1]
  const article = useQuery({
    queryKey: helpKeys.article(side, slug),
    queryFn: () => helpApi.article(side, slug),
    staleTime: HELP_STALE_MS,
    retry: helpQueryRetry,
  })

  return (
    <SideDrawer onClose={onClose} loading={article.isPending}>
      {(close) => (
        <>
          <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border px-6 py-5">
            <div className="min-w-0 space-y-1">
              {trail.length > 1 ? (
                <button
                  type="button"
                  onClick={() => setTrail((t) => t.slice(0, -1))}
                  className="inline-flex items-center gap-1 text-xs font-medium text-foreground-muted hover:text-foreground"
                >
                  <ArrowLeft size={12} aria-hidden="true" />
                  Back
                </button>
              ) : null}
              <p className="text-xs font-semibold uppercase tracking-wide text-foreground-subtle">Help</p>
              <h2 className="font-display text-base font-bold text-foreground">
                {article.data?.title ?? (article.isError ? 'Help' : 'Loading…')}
              </h2>
            </div>
            <button
              type="button"
              onClick={close}
              aria-label="Close"
              className="shrink-0 rounded-lg p-1.5 text-foreground-subtle transition-colors hover:bg-surface-hover hover:text-foreground"
            >
              <X size={18} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 scrollbar-thin">
            {article.isError ? (
              <ErrorState
                title="Couldn't load this article"
                text={apiErrorMessage(article.error)}
                onRetry={() => void article.refetch()}
                retrying={article.isFetching}
              />
            ) : article.data ? (
              <HelpMarkdown
                markdown={stripLeadingTitle(article.data.body ?? '')}
                onHelpLink={(next) => setTrail((t) => (t[t.length - 1] === next ? t : [...t, next]))}
              />
            ) : null}
          </div>

          <div className="shrink-0 border-t border-border px-6 py-4">
            <Link
              to={helpPath(slug)}
              onClick={close}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
            >
              <ExternalLink size={14} aria-hidden="true" />
              Open in help centre
            </Link>
          </div>
        </>
      )}
    </SideDrawer>
  )
}

/**
 * "?" in the top bar: the help article for the page you are on (the visible
 * article whose `routes` is the longest prefix of the path). Hidden when no
 * article matches or the server has no help.
 */
export default function HelpButton() {
  const { pathname } = useLocation()
  const help = useHelpArticles()
  const [openSlug, setOpenSlug] = useState<string | null>(null)

  const match = help.available ? findArticleForPath(help.articles, pathname) : null
  if (!help.side || (!match && !openSlug)) return null

  return (
    <>
      {match ? (
        <button
          type="button"
          data-testid="help-button"
          onClick={() => setOpenSlug(match.slug)}
          className="relative p-1.5 rounded-lg text-foreground-muted hover:text-foreground hover:bg-surface-hover transition-all before:absolute before:-inset-2 before:content-['']"
          aria-label={`Help: ${match.title}`}
          title={`Help: ${match.title}`}
        >
          <CircleHelp size={16} />
        </button>
      ) : null}
      {openSlug ? (
        <HelpArticleDrawer side={help.side} slug={openSlug} onClose={() => setOpenSlug(null)} />
      ) : null}
    </>
  )
}
